/**
 * Utility functions for event date formatting across different locales
 */

/**
 * Get locale string based on language code
 */
export const getEventLocale = lang => {
  if (lang === 'de') return 'de-DE';
  if (lang === 'ae') return 'ar-AE';
  return 'en-US';
};

/**
 * Format date with locale and options, removing periods
 */
export const formatEventDate = (date, locale, options = {}) => {
  return new Date(date)
    .toLocaleDateString(locale, {
      month: 'short',
      day: 'numeric',
      ...options,
    })
    .replaceAll('.', '');
};

/**
 * Format German date with proper period handling
 */
export const formatGermanEventDate = (date, locale) => {
  let formatted = new Date(date).toLocaleString(locale, {
    month: 'short',
    day: 'numeric',
  });

  if (formatted.endsWith('.')) {
    formatted = formatted.slice(0, -1);
  }

  return formatted;
};

/**
 * Format German duration with multi-day support
 */
export const formatGermanDuration = (startDate, finishDate, locale) => {
  const getDay = date => new Date(date).getDate();
  const getMonth = date => new Date(date).getMonth();
  const getYear = date => new Date(date).getFullYear();

  const isMultiDay =
    getDay(startDate) !== getDay(finishDate) || getMonth(startDate) !== getMonth(finishDate);

  if (isMultiDay) {
    const options = { day: '2-digit', month: '2-digit', year: 'numeric' };
    const start = new Date(startDate).toLocaleDateString(locale, options);
    const finish = new Date(finishDate).toLocaleDateString(locale, options);
    return `von ${start} bis ${finish}`;
  }

  return `${formatGermanEventDate(startDate, locale)} ${getYear(startDate)}`;
};

/**
 * Format date range with proper language ordering (Arabic vs others)
 */
export const formatEventDateRange = (startFormatted, finishFormatted, lang) => {
  return lang === 'ae'
    ? `${finishFormatted} – ${startFormatted}`
    : `${startFormatted} – ${finishFormatted}`;
};

/**
 * Format same day event date
 */
export const formatSameDayEventDate = (startDate, finishDate, preparedStartMonth, lang) => {
  const getDay = date => new Date(date).getDate();
  const getYear = date => new Date(date).getFullYear();

  const dayStart = getDay(startDate);
  const year = getYear(finishDate);

  return lang === 'ae'
    ? `${dayStart} ${preparedStartMonth} ${year}`
    : `${preparedStartMonth} ${dayStart}, ${year}`;
};

/**
 * Format different days event date (same month)
 */
export const formatDifferentDaysEventDate = (startDate, finishDate, preparedStartMonth, lang) => {
  const getDay = date => new Date(date).getDate();
  const getYear = date => new Date(date).getFullYear();

  const dayStart = getDay(startDate);
  const dayFinish = getDay(finishDate);
  const year = getYear(finishDate);

  return lang === 'ae'
    ? `${dayFinish} – ${dayStart} ${preparedStartMonth} ${year}`
    : `${preparedStartMonth} ${dayStart} – ${dayFinish}, ${year}`;
};

/**
 * Format different months event date
 */
export const formatDifferentMonthsEventDate = (startDate, finishDate, locale, lang) => {
  const startFormatted = formatEventDate(startDate, locale);
  const finishFormatted = formatEventDate(finishDate, locale, { year: 'numeric' });

  return formatEventDateRange(startFormatted, finishFormatted, lang);
};

/**
 * Format different years event date
 */
export const formatDifferentYearsEventDate = (startDate, finishDate, locale, lang) => {
  const startFormatted = formatEventDate(startDate, locale, { year: 'numeric' });
  const finishFormatted = formatEventDate(finishDate, locale, { year: 'numeric' });

  return formatEventDateRange(startFormatted, finishFormatted, lang);
};

/**
 * Main function to get event duration string
 */
export const getEventDuration = (startDate, finishDate, lang) => {
  const locale = getEventLocale(lang);
  const getDay = date => new Date(date).getDate();
  const getMonth = date => new Date(date).getMonth();
  const getYear = date => new Date(date).getFullYear();

  // German has special formatting rules
  if (lang === 'de') {
    return formatGermanDuration(startDate, finishDate, locale);
  }

  const preparedStartMonth = formatEventDate(startDate, locale, { day: undefined });

  // Different years
  if (getYear(startDate) !== getYear(finishDate)) {
    return formatDifferentYearsEventDate(startDate, finishDate, locale, lang);
  }

  // Different months
  if (getMonth(startDate) !== getMonth(finishDate)) {
    return formatDifferentMonthsEventDate(startDate, finishDate, locale, lang);
  }

  // Different days
  if (getDay(startDate) !== getDay(finishDate)) {
    return formatDifferentDaysEventDate(startDate, finishDate, preparedStartMonth, lang);
  }

  // Same day
  return formatSameDayEventDate(startDate, finishDate, preparedStartMonth, lang);
};
